import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { 
  Package, RefreshCw, Search, Filter,
  Clock, CheckCircle, AlertCircle, Edit2, ExternalLink,
  Download, Calendar, Send
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import BackorderUpdateModal from './BackorderUpdateModal';
import PlaceOrderModal from './PlaceOrderModal';
import { format } from 'date-fns';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';

const BackorderManagement = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [backorderItems, setBackorderItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showPlaceOrderModal, setShowPlaceOrderModal] = useState(false);
  const [itemsToPlace, setItemsToPlace] = useState([]);
  const [placingOnOrder, setPlacingOnOrder] = useState(false);
  const [summary, setSummary] = useState({
    totalOrders: 0,
    totalPendingItems: 0,
    totalFulfilledItems: 0,
    totalPartiallyFulfilledItems: 0,
    lastSyncAt: null
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    if (statusParam) {
      setStatusFilter(statusParam);
    }
    fetchBackorderData();
  }, [location.search]);

  useEffect(() => {
    applyFilters();
  }, [backorderItems, searchTerm, statusFilter]);

  const fetchBackorderData = async () => {
    try {
      setLoading(true);
      const { data: summaryData, error: summaryError } = await supabase.rpc('get_backorder_summary');
      if (summaryError) throw summaryError;
      setSummary(summaryData);

      const { data: itemsData, error: itemsError } = await supabase.rpc('get_all_backorder_items', {
        status_filter: null,
        search_term: null
      });
      if (itemsError) throw itemsError;
      setBackorderItems(itemsData || []);
    } catch (error) {
      console.error('Error fetching backorder data:', error);
      showError(error, 'Failed to load backorder data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...backorderItems];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.order_number?.toLowerCase().includes(searchLower) ||
        item.customer_name?.toLowerCase().includes(searchLower) ||
        item.part_number?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredItems(filtered);
  };

  const handleManualSync = async () => {
    try {
      setSyncing(true);
      showNotification('Starting backorder sync from OrderBoard...', 'info');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      // Call the new FastAPI sync endpoint instead of Supabase edge function
      const response = await fetch('/api/v1/sync/backorders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          `Sync endpoint returned an unexpected response (${response.status}). ` +
          'The API may be unreachable or misconfigured.'
        );
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Sync request failed');
      }

      const data = await response.json();
      
      if (data.success) {
        showNotification(
          `Sync complete: ${data.items_updated} items updated out of ${data.items_checked} checked`,
          'success'
        );
        await fetchBackorderData();
      } else {
        showError(
          new Error(data.errors?.join(', ') || 'Sync failed'),
          'Sync completed with errors'
        );
      }
    } catch (error) {
      console.error('Error syncing backorders:', error);
      showError(error, 'Failed to sync backorders');
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateItem = (item) => {
    setSelectedItem(item);
    setShowUpdateModal(true);
  };

  const handleUpdateComplete = async () => {
    setShowUpdateModal(false);
    setSelectedItem(null);
    await fetchBackorderData();
  };

  const handleToggleItemSelection = (item) => {
    const itemKey = `${item.order_id}-${item.part_number}`;
    if (selectedItems.includes(itemKey)) {
      setSelectedItems(selectedItems.filter(key => key !== itemKey));
    } else {
      setSelectedItems([...selectedItems, itemKey]);
    }
  };

  const handleToggleSelectAll = () => {
    const selectableItems = filteredItems.filter(item => !item.orderboard_id);
    if (selectedItems.length === selectableItems.length && selectableItems.length > 0) {
      setSelectedItems([]);
    } else {
      setSelectedItems(selectableItems.map(item => `${item.order_id}-${item.part_number}`));
    }
  };

  const isItemSelected = (item) => {
    return selectedItems.includes(`${item.order_id}-${item.part_number}`);
  };

  const handlePlaceOnOrder = (items) => {
    setItemsToPlace(items);
    setShowPlaceOrderModal(true);
  };

  const handleConfirmPlaceOrder = async (items, freightType = 'Airbag') => {
    try {
      setPlacingOnOrder(true);
      
      const orderBoardItems = items.map(item => ({
        partNumber: item.part_number,
        quantity: item.backorder_qty,
        division: 'Hydraulics',
        notes: `Customer: ${item.customer_name} | Order: ${item.order_number}`,
        description: item.description || '',
        freightType: freightType
      }));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      const response = await fetch('/api/v1/orderboard/items/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ items: orderBoardItems })
      });

      if (!response.ok) {
        throw new Error(`OrderBoard API error: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        showNotification(
          `Successfully placed ${items.length} ${items.length === 1 ? 'item' : 'items'} on OrderBoard`,
          'success'
        );
        
        setSelectedItems([]);
        setShowPlaceOrderModal(false);
        setItemsToPlace([]);
        await fetchBackorderData();
      } else {
        throw new Error(result.message || 'Failed to place items on OrderBoard');
      }
    } catch (error) {
      console.error('Error placing items on OrderBoard:', error);
      showError(error, 'Failed to place items on OrderBoard');
    } finally {
      setPlacingOnOrder(false);
    }
  };

  const getSelectedItemsData = () => {
    return filteredItems.filter(item => isItemSelected(item));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-300',
        icon: Clock
      },
      partially_fulfilled: {
        bg: 'bg-orange-100',
        text: 'text-orange-800',
        border: 'border-orange-300',
        icon: AlertCircle
      },
      fulfilled: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-300',
        icon: CheckCircle
      },
      cancelled: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-300',
        icon: AlertCircle
      }
    };

    const config = badges[status] || badges.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const exportToCSV = () => {
    const headers = ['Customer', 'Order Number', 'Part Number', 'Description', 'Ordered Qty', 'Status', 'Fulfilled Qty', 'Last Updated'];
    const rows = filteredItems.map(item => [
      item.customer_name,
      item.order_number,
      item.part_number,
      item.description,
      item.backorder_qty,
      item.status,
      item.fulfilled_qty,
      item.updated_at ? format(new Date(item.updated_at), 'yyyy-MM-dd HH:mm') : 'N/A'
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backorders_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    showNotification('Backorder data exported to CSV', 'success');
  };

  const selectableItemsCount = filteredItems.filter(item => !item.orderboard_id).length;
  const allSelectableSelected = selectableItemsCount > 0 && selectedItems.length === selectableItemsCount;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Backorders"
        description="Monitor and manage all backorder items"
        actions={
          <>
            <Button
              variant="secondary"
              onClick={exportToCSV}
              icon={<Download className="w-4 h-4" />}
            >
              Export CSV
            </Button>
            <Button
              variant="primary"
              onClick={handleManualSync}
              disabled={syncing}
              icon={<RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />}
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalOrders}</p>
            </div>
            <Package className="w-8 h-8 text-gray-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 border-l-4 border-l-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalPendingItems}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 border-l-4 border-l-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Partial</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalPartiallyFulfilledItems}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 border-l-4 border-l-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Fulfilled</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalFulfilledItems}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Last Sync</p>
              <p className="text-xs font-medium text-gray-900">
                {summary.lastSyncAt 
                  ? format(new Date(summary.lastSyncAt), 'MMM d, HH:mm')
                  : 'Never'}
              </p>
            </div>
            <Calendar className="w-8 h-8 text-gray-500" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0 md:space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by customer, order #, part number, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent text-gray-900"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="text-gray-600" size={20} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent text-gray-900"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="partially_fulfilled">Partially Fulfilled</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {filteredItems.length} of {backorderItems.length} backorder items
          </div>
          {selectedItems.length > 0 && (
            <button
              onClick={() => handlePlaceOnOrder(getSelectedItemsData())}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
            >
              <Send className="w-4 h-4 mr-2" />
              Place {selectedItems.length} Selected on Order
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <RefreshCw className="w-8 h-8 text-brand animate-spin" />
          <span className="ml-3 text-gray-700">Loading backorder data...</span>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No backorder items found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={allSelectableSelected}
                      onChange={handleToggleSelectAll}
                      disabled={selectableItemsCount === 0}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      title={selectableItemsCount === 0 ? 'No items available to select' : 'Select all'}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Part Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((item, index) => (
                  <motion.tr
                    key={`${item.order_id}-${item.part_number}-${index}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-3 py-4 text-center">
                      {!item.orderboard_id ? (
                        <input
                          type="checkbox"
                          checked={isItemSelected(item)}
                          onChange={() => handleToggleItemSelection(item)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          On OB
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{item.customer_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.order_number}</div>
                      <div className="text-xs text-gray-500">
                        {format(new Date(item.order_created_at), 'MMM d, yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">{item.part_number}</div>
                      {item.orderboard_id && (
                        <div className="text-xs text-gray-500">OB-{item.orderboard_id}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={item.description}>
                        {item.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900">
                        <span className="font-semibold">{item.fulfilled_qty}</span>
                        <span className="text-gray-500"> / {item.backorder_qty}</span>
                      </div>
                      {item.status === 'partially_fulfilled' && (
                        <div className="text-xs text-orange-600">
                          {Math.round((item.fulfilled_qty / item.backorder_qty) * 100)}% complete
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {item.updated_at 
                          ? format(new Date(item.updated_at), 'MMM d, HH:mm')
                          : 'N/A'}
                      </div>
                      {item.last_sync_at && (
                        <div className="text-xs text-gray-500">
                          Sync: {format(new Date(item.last_sync_at), 'MMM d, HH:mm')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        {!item.orderboard_id && (
                          <button
                            onClick={() => handlePlaceOnOrder([item])}
                            className="text-green-600 hover:text-green-900"
                            title="Place on Order"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleUpdateItem(item)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Update status"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {item.orderboard_id && (
                          <a
                            href={`http://warehouse.mhs.com.au/orderboard/items/${item.orderboard_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-900"
                            title="View in OrderBoard"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showUpdateModal && selectedItem && (
        <BackorderUpdateModal
          item={selectedItem}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedItem(null);
          }}
          onUpdate={handleUpdateComplete}
        />
      )}

      {/* Update Modal */}
      {showUpdateModal && selectedItem && (
        <BackorderUpdateModal
          item={selectedItem}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedItem(null);
          }}
          onUpdate={handleUpdateComplete}
        />
      )}

      {/* Place Order Modal */}
      {showPlaceOrderModal && itemsToPlace.length > 0 && (
        <PlaceOrderModal
          items={itemsToPlace}
          onClose={() => {
            setShowPlaceOrderModal(false);
            setItemsToPlace([]);
          }}
          onConfirm={handleConfirmPlaceOrder}
          loading={placingOnOrder}
        />
      )}
    </div>
  );
};

export default BackorderManagement;
